import React, { useEffect, useRef, useState } from 'react';
import { PanelErrorBoundary, Timeline, Composer } from '../kit';
import DrillOver from '../../shared/DrillOver';
import { STREAM, ANCHOR_OFFSET } from '../layout';
import CardHeader from './CardHeader';
import CountBadge from './CountBadge';

// Events shown before "Load more"; "Full history →" opens every event.
export const ACTIVITY_VISIBLE = 5;

// Activity — the loudest element on the page (spec §7): the event stream with the
// composer directly beneath it, so a reply is written where the conversation is
// read. Self-fetches fn_timeline (cursor-paginated) inside Timeline; writes only
// through the kit Composer — email via fn_compose_send, or a team note.

/**
 * @param {object} props
 * @param {string} props.clientId
 * @param {number} [props.focusSignal]  A counter the page bumps (the header's Compose
 *   button): scroll the composer into view, clear of the sticky header, and focus it.
 * @param {string} [props.query]  The page search: filters the loaded events.
 * @param {React.ReactNode} [props.search]  The page's search box, shown with the filter tags once "Filter options"
 *   is opened, above the tags (it filters this stream, Work and Requests, so it sits with the stream it is most about).
 */
export default function ActivityCard({ clientId, focusSignal = 0, query = '', search = null }) {
  // Bumped after a send so the stream reloads in place and shows what was just written.
  const [refreshKey, setRefreshKey] = useState(0);
  const composerRef = useRef(null);
  // How many events the stream has loaded, and whether older ones remain (for the count badge).
  const [stats, setStats] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  // The search box and the filter tags stay hidden until "Filter options" is opened.
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Where the stream draws its filter tags: a spot inside the filter box.
  const [tagsHost, setTagsHost] = useState(null);
  const lastSignal = useRef(focusSignal);

  useEffect(() => {
    if (focusSignal === lastSignal.current) return;
    lastSignal.current = focusSignal;
    // scroll-margin-top (ANCHOR_OFFSET) keeps it from landing under the header.
    composerRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [focusSignal]);

  const filtersApplied = query.trim() !== '' || !!stats?.tagsActive;

  return (
    <section aria-label="Activity" className={STREAM}>
      <CardHeader
        title="Activity"
        action={(
          <div className="flex flex-wrap items-center justify-end gap-3">
            {/* "Filter options" is a small button before "Full history →", filled while the panel is closed and
                unfilled while it is open. Always offered: the search also filters Work and Requests, so it must be
                reachable when the stream itself is empty. A dot says a filter is still applied while closed. */}
            <button
              type="button" onClick={() => setFiltersOpen((o) => !o)} aria-expanded={filtersOpen} aria-controls="activity-filters"
              data-testid="activity-filter-toggle"
              className={`inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-sm text-gray-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand dark:text-gray-200 ${
                filtersOpen
                  ? 'border-gray-200 bg-transparent hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800'
                  : 'border-gray-200 bg-gray-100 hover:bg-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700'
              }`}
            >
              <svg className="h-4 w-4 text-gray-500 dark:text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              Filter options
              {!filtersOpen && filtersApplied && (
                <span data-testid="activity-filters-on" role="img" aria-label="filters on" className="h-2 w-2 rounded-full bg-brand" />
              )}
            </button>
            {stats && stats.loaded > 0 && (
              <>
                <button
                  type="button" onClick={() => setHistoryOpen(true)} data-testid="activity-full-history"
                  className="rounded text-sm text-brand underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                >
                  Full history →
                </button>
                <CountBadge count={stats.loaded} more={stats.hasMore} noun="event" testId="activity-count" />
              </>
            )}
          </div>
        )}
      />
      {/* Opened from the button above: a box under the header holding the search box, then the stream's filter
          tags (drawn here by the stream). */}
      {filtersOpen && (
        <div id="activity-filters" data-testid="activity-filter-box" className="mt-3 space-y-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
          {search ? <div data-testid="activity-search">{search}</div> : null}
          <div ref={setTagsHost} data-testid="activity-filter-tags" className="empty:hidden" />
        </div>
      )}
      <div className="mt-4">
        <PanelErrorBoundary label="Event stream">
          <Timeline
            clientId={clientId} variant="stream" refreshKey={refreshKey} query={query}
            initialVisible={ACTIVITY_VISIBLE} showFilters={filtersOpen} filtersHost={tagsHost} onStats={setStats}
            emptyMessage="No recent activity — write the first message below."
          />
        </PanelErrorBoundary>
      </div>
      <div ref={composerRef} data-testid="activity-composer" className={`mt-5 border-t border-gray-100 pt-5 dark:border-gray-800 ${ANCHOR_OFFSET}`}>
        <PanelErrorBoundary label="Composer">
          <Composer clientId={clientId} noteToggle focusSignal={focusSignal} onSent={() => setRefreshKey((k) => k + 1)} />
        </PanelErrorBoundary>
      </div>
      {/* The whole stream, in the app's one slide-over (scrim, Esc, focus trap); it loads older pages itself. */}
      {historyOpen && (
        <DrillOver
          stack={[{
            label: 'Activity — full history',
            body: <Timeline clientId={clientId} variant="stream" query={query} emptyMessage="No recent activity." />,
          }]}
          onBack={() => setHistoryOpen(false)}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </section>
  );
}
